// @ts-strict-ignore
import React, { useEffect, useState, type ComponentProps } from 'react';
import { useHotkeys } from 'react-hotkeys-hook';
import AutoSizer from 'react-virtualized-auto-sizer';

import * as monthUtils from 'loot-core/src/shared/months';

import { View } from '../common/View';

import { useBudgetMonthCount } from './BudgetMonthCountContext';
import { BudgetPageHeader } from './BudgetPageHeader';
import { BudgetTable } from './BudgetTable';

function getNumPossibleMonths(width: number) {
  const estimatedTableWidth = width - 200;

  if (estimatedTableWidth < 500) {
    return 1;
  } else if (estimatedTableWidth < 750) {
    return 2;
  } else if (estimatedTableWidth < 1000) {
    return 3;
  } else if (estimatedTableWidth < 1250) {
    return 4;
  } else if (estimatedTableWidth < 1500) {
    return 5;
  }

  return 6;
}

type DynamicBudgetTableInnerProps = {
  width: number;
  height: number;
} & DynamicBudgetTableProps;
const DynamicBudgetTableInner = ({
  type,
  width,
  height,
  prewarmStartMonth,
  startMonth,
  maxMonths = 3,
  monthBounds,
  sparseMonths,
  onMonthSelect,
  headerLeft,
  ...props
}: DynamicBudgetTableInnerProps) => {
  const { setDisplayMax } = useBudgetMonthCount();
  // Track the month the user actually clicked (not the window-start after clamping)
  const [activeMonth, setActiveMonth] = useState<string | undefined>();

  const numPossible = getNumPossibleMonths(width);
  const numMonths = Math.min(numPossible, maxMonths);
  // In sparse mode, only as many columns as sparse months available from startMonth
  const effectiveNumMonths = sparseMonths && sparseMonths.length > 0
    ? (() => {
        const idx = sparseMonths.indexOf(startMonth);
        return idx >= 0 ? Math.min(numMonths, sparseMonths.length - idx) : 1;
      })()
    : numMonths;
  const maxWidth = 200 + 500 * effectiveNumMonths;

  useEffect(() => {
    setDisplayMax(numPossible);
  }, [numPossible]);

  // Center on today (or nearest sparse month) on initial mount
  useEffect(() => {
    _centerOnMonth(monthUtils.currentMonth());
  }, []);

  function getValidMonth(month) {
    if (sparseMonths && sparseMonths.length > 0) {
      // Clamp to sparse list bounds
      if (month < sparseMonths[0]) return sparseMonths[0];
      if (month > sparseMonths[sparseMonths.length - 1]) return sparseMonths[sparseMonths.length - 1];
      return month;
    }
    const start = monthBounds.start;
    const end = monthUtils.subMonths(monthBounds.end, numMonths - 1);
    if (month < start) return start;
    if (month > end) return end;
    return month;
  }

  // For sparse mode: find the index of the window start in the sparse list
  function getSparseWindowStart(centerMonth: string): string {
    if (!sparseMonths || sparseMonths.length === 0) return centerMonth;
    // Find the closest sparse month to centerMonth
    let closestIdx = 0;
    for (let i = 0; i < sparseMonths.length; i++) {
      if (sparseMonths[i] <= centerMonth) closestIdx = i;
    }
    // Place clicked month right-of-center (floor(n/2) offset), then clamp to bounds.
    // For even n=2: offset=1 → clicked month at right. For odd n=3: offset=1 → true center.
    const offset = Math.floor(numMonths / 2);
    const idealStart = closestIdx - offset;
    const clampedToEnd = Math.min(idealStart, sparseMonths.length - numMonths);
    const windowStart = Math.max(0, clampedToEnd);
    return sparseMonths[windowStart];
  }

  function _onMonthSelect(month) {
    onMonthSelect(getValidMonth(month), numMonths);
  }

  function _centerOnMonth(month) {
    if (sparseMonths && sparseMonths.length > 0) {
      setActiveMonth(month);
      onMonthSelect(getSparseWindowStart(month), numMonths);
      return;
    }
    const offset = Math.floor((numMonths - 1) / 2);
    const centeredStart = monthUtils.subMonths(month, offset);
    onMonthSelect(getValidMonth(centeredStart), numMonths);
  }

  function _onPrev() {
    if (sparseMonths && sparseMonths.length > 0) {
      const idx = sparseMonths.indexOf(startMonth);
      if (idx > 0) {
        setActiveMonth(sparseMonths[idx - 1]);
        onMonthSelect(sparseMonths[idx - 1], numMonths);
      }
    } else {
      _onMonthSelect(monthUtils.prevMonth(startMonth));
    }
  }

  function _onNext() {
    if (sparseMonths && sparseMonths.length > 0) {
      const idx = sparseMonths.indexOf(startMonth);
      if (idx < sparseMonths.length - 1) {
        setActiveMonth(sparseMonths[idx + 1]);
        onMonthSelect(sparseMonths[idx + 1], numMonths);
      }
    } else {
      _onMonthSelect(monthUtils.nextMonth(startMonth));
    }
  }

  useHotkeys('left', _onPrev, { preventDefault: true, scopes: ['app'] }, [_onPrev]);
  useHotkeys('right', _onNext, { preventDefault: true, scopes: ['app'] }, [_onNext]);
  useHotkeys(
    '0',
    () => { _centerOnMonth(monthUtils.currentMonth()); },
    { preventDefault: true, scopes: ['app'] },
    [_centerOnMonth],
  );

  return (
    <View
      style={{
        width,
        height,
        alignItems: 'center',
        opacity: width <= 0 || height <= 0 ? 0 : 1,
      }}
    >
      <View style={{ width: '100%', maxWidth }}>
        <BudgetPageHeader
          startMonth={prewarmStartMonth}
          numMonths={numMonths}
          monthBounds={monthBounds}
          sparseMonths={sparseMonths}
          activeMonth={activeMonth}
          onMonthSelect={_centerOnMonth}
          headerLeft={headerLeft}
        />
        <BudgetTable
          type={type}
          prewarmStartMonth={prewarmStartMonth}
          startMonth={startMonth}
          numMonths={numMonths}
          monthBounds={monthBounds}
          sparseMonths={sparseMonths}
          {...props}
        />
      </View>
    </View>
  );
};

DynamicBudgetTableInner.displayName = 'DynamicBudgetTableInner';

type DynamicBudgetTableProps = Omit<
  ComponentProps<typeof BudgetTable>,
  'numMonths'
> & {
  maxMonths: number;
  sparseMonths?: string[];
  onMonthSelect: (month: string, numMonths: number) => void;
  headerLeft?: React.ReactNode;
};

export const DynamicBudgetTable = (props: DynamicBudgetTableProps) => {
  return (
    <AutoSizer>
      {({ width, height }) => (
        <DynamicBudgetTableInner width={width} height={height} {...props} />
      )}
    </AutoSizer>
  );
};

DynamicBudgetTable.displayName = 'DynamicBudgetTable';
